/**
 * FirmaOverview – Widget-Dashboard
 * ==================================
 * Alle Inhalte der Übersicht als konfigurierbare, verschiebbare Widgets.
 * Layout wird in localStorage gespeichert.
 */
import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import type { Firma, PrimaryContact, Teilnehmer, FomAnsprechpartner } from '@/types'
import type { TnFilter } from './FirmaTeilnehmer'
import { useAuth } from '@/contexts/AuthContext'
import { useFirmaContext } from '@/contexts/FirmaContext'
import { assetUrl } from '@/utils/assetUrl'
import Avatar from '@/components/Avatar'
import {
  User, Mail, Phone, ChevronLeft, ChevronRight, Newspaper, ExternalLink,
  CalendarDays, Settings, GripVertical, X,
  Check, Users, Clock, Star,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import BlackboardWidget from './BlackboardWidget'
import { ReactGridLayout, WidthProvider, type LayoutItem as RGLItem } from 'react-grid-layout/legacy'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const ResponsiveGridLayout = WidthProvider(ReactGridLayout)

// ── Types ─────────────────────────────────────────────────────

interface Props {
  firma: Firma
  primaryContact: PrimaryContact | null
  teilnehmer: Teilnehmer[]
  accountId: string
  onShowTeilnehmer?: (filter?: TnFilter) => void
  onShowDualMatch?: () => void
  fomAnsprechpartner?: FomAnsprechpartner[]
}

type WidgetId = 'welcome' | 'studierende' | 'ansprechpartner' | 'quicklinks' | 'blackboard' | 'vorlesungen' | 'noten' | 'dualmatch' | 'events' | 'news' | 'magazine'

type WidgetSpan = 1 | 2 | 3

interface WidgetDef {
  id: WidgetId
  label: string
  description: string
  icon: string
  defaultVisible: boolean
  defaultSpan: WidgetSpan
}

const WIDGET_DEFS: WidgetDef[] = [
  { id: 'welcome', label: 'Willkommen', description: 'Begrüßung mit Firmenlogo', icon: '👋', defaultVisible: true, defaultSpan: 3 },
  { id: 'studierende', label: 'Studierende', description: 'Anzahl aktiver Studierender', icon: '🎓', defaultVisible: true, defaultSpan: 1 },
  { id: 'ansprechpartner', label: 'Ansprechpartner', description: 'Ihr Hauptansprechpartner', icon: '🤝', defaultVisible: true, defaultSpan: 1 },
  { id: 'quicklinks', label: 'Schnellzugriff', description: 'Direktlinks zu allen Bereichen', icon: '⚡', defaultVisible: true, defaultSpan: 1 },
  { id: 'blackboard', label: 'News', description: 'Aktuelle Mitteilungen und Neuigkeiten', icon: '📋', defaultVisible: true, defaultSpan: 3 },
  { id: 'vorlesungen', label: 'Vorlesungsplan', description: 'Nächste Vorlesungen Ihrer Studierenden', icon: '📚', defaultVisible: true, defaultSpan: 3 },
  { id: 'noten', label: 'Neue Noten', description: 'Letzte Prüfungsergebnisse Ihrer Studierenden', icon: '📊', defaultVisible: true, defaultSpan: 3 },
  { id: 'dualmatch', label: 'DualMatch', description: 'Passende Auszubildende finden', icon: '🎯', defaultVisible: true, defaultSpan: 3 },
  { id: 'events', label: 'FOM Events', description: 'Kommende Veranstaltungen aus Dynamics 365', icon: '📅', defaultVisible: true, defaultSpan: 3 },
  { id: 'news', label: 'FOM News', description: 'Aktuelle Nachrichten der FOM', icon: '📰', defaultVisible: true, defaultSpan: 2 },
  { id: 'magazine', label: 'Online Magazin & Talente', description: 'Talente-Magazin und FOM Online-Magazin', icon: '📖', defaultVisible: true, defaultSpan: 2 },
]

const GRID_COLS = 3
const ROW_HEIGHT = 100  // px pro Grid-Row

// Default-Höhen pro Widget (in Grid-Rows)
const WIDGET_HEIGHTS: Record<WidgetId, number> = {
  welcome:        4,
  studierende:    2,
  ansprechpartner:2,
  quicklinks:     2,
  blackboard:     4,
  vorlesungen:    4,
  noten:          4,
  dualmatch:      4,
  events:         5,
  news:           4,
  magazine:       4,
}

// Min/Max-Constraints — bewusst weich, damit User die Groesse frei
// waehlen kann. maxW=3 weil GRID_COLS=3, kleiner wird hier nicht
// blockiert (min 1 Spalte/2 Rows als sinnvolles Minimum).
const WIDGET_CONSTRAINTS: Record<WidgetId, { minW: number; minH: number; maxW: number }> = {
  welcome:        { minW: 1, minH: 2, maxW: 3 },
  studierende:    { minW: 1, minH: 2, maxW: 3 },
  ansprechpartner:{ minW: 1, minH: 2, maxW: 3 },
  quicklinks:     { minW: 1, minH: 2, maxW: 3 },
  blackboard:     { minW: 1, minH: 2, maxW: 3 },
  vorlesungen:    { minW: 1, minH: 2, maxW: 3 },
  noten:          { minW: 1, minH: 2, maxW: 3 },
  dualmatch:      { minW: 1, minH: 2, maxW: 3 },
  events:         { minW: 1, minH: 2, maxW: 3 },
  news:           { minW: 1, minH: 2, maxW: 3 },
  magazine:       { minW: 1, minH: 2, maxW: 3 },
}

interface LayoutItem {
  i: WidgetId
  x: number
  y: number
  w: number
  h: number
}

interface WidgetPosition {
  x: number
  y: number
  w: number
}

// LayoutState behalten wir struktur-kompatibel zur API.
// Neu: items[] mit x/y/w/h. positions+spans bleiben für Migrations-Periode.
interface LayoutState {
  visible: WidgetId[]
  order: WidgetId[]
  spans?: Partial<Record<WidgetId, WidgetSpan>>
  positions?: Partial<Record<WidgetId, WidgetPosition>>
  items?: LayoutItem[]
}

/** Packt Widgets row-by-row in ein 3-Spalten-Grid (Default-Layout). */
function packItems(visible: WidgetId[], order: WidgetId[]): LayoutItem[] {
  const out: LayoutItem[] = []
  const ids = order.filter(id => visible.includes(id))
  let row = 0
  let col = 0
  let rowH = 0
  for (const id of ids) {
    const def = WIDGET_DEFS.find(d => d.id === id)!
    const w = def.defaultSpan as number
    const h = WIDGET_HEIGHTS[id]
    if (col + w > GRID_COLS) { row += rowH; col = 0; rowH = 0 }
    out.push({ i: id, x: col, y: row, w, h })
    col += w
    rowH = Math.max(rowH, h)
    if (col >= GRID_COLS) { row += rowH; col = 0; rowH = 0 }
  }
  return out
}

/** Migriert alte positions+spans → items[] indem Default-Höhen ergänzt werden. */
function legacyLayoutToItems(visible: WidgetId[], positions: Partial<Record<WidgetId, WidgetPosition>>): LayoutItem[] {
  const out: LayoutItem[] = []
  for (const id of visible) {
    const p = positions[id]
    if (!p) continue
    out.push({ i: id, x: p.x, y: p.y, w: p.w, h: WIDGET_HEIGHTS[id] })
  }
  return out
}

// ── Favoriten (API-gespeichert) ──────────────────────────────

function useFavorites() {
  const [favs, setFavs] = useState<number[]>([])
  const loaded = useRef(false)

  // Initialen Wert kommt über usePreferences → setFavs (s. unten)
  const setInitial = useCallback((ids: number[]) => {
    if (!loaded.current) { setFavs(ids); loaded.current = true }
  }, [])

  const toggle = useCallback((id: number) => {
    setFavs(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      fetch('/api/user/preferences/favorites', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(next),
      }).catch(() => {})
      return next
    })
  }, [])
  const isFav = useCallback((id: number) => favs.includes(id), [favs])
  return { favs, toggle, isFav, setInitial }
}

// ── Layout (API-gespeichert) ─────────────────────────────────

function parseLayout(saved: LayoutState | null): LayoutState {
  const allIds = WIDGET_DEFS.map(w => w.id)
  const isKnown = (id: string): id is WidgetId => (allIds as string[]).includes(id)

  if (saved?.visible && saved?.order) {
    const newIds = allIds.filter(id => !saved.order.includes(id))
    const order: WidgetId[] = [...saved.order.filter(isKnown), ...newIds]
    const newVisible = newIds.filter(id => WIDGET_DEFS.find(w => w.id === id)?.defaultVisible)
    const visible: WidgetId[] = [...saved.visible.filter(isKnown), ...newVisible]

    // 1) Wenn items[] schon vorhanden → benutzen, nur fehlende neu hinzufügen
    if (saved.items && saved.items.length > 0) {
      const knownItems = saved.items
        .filter(it => isKnown(it.i)).filter(it => visible.includes(it.i))
        // Welcome-Banner: gespeicherte Höhe auf neue Mindesthöhe anheben (nie verkleinern)
        .map(it => it.i === 'welcome' ? { ...it, h: Math.max(it.h, WIDGET_HEIGHTS.welcome) } : it)
      const missingIds = visible.filter(id => !knownItems.find(it => it.i === id))
      if (missingIds.length > 0) {
        const maxY = knownItems.reduce((m, it) => Math.max(m, it.y + it.h), 0)
        let col = 0
        const newItems: LayoutItem[] = []
        for (const id of missingIds) {
          const def = WIDGET_DEFS.find(d => d.id === id)!
          const w = def.defaultSpan as number
          if (col + w > GRID_COLS) col = 0
          newItems.push({ i: id, x: col, y: maxY, w, h: WIDGET_HEIGHTS[id] })
          col += w
        }
        return { visible, order, items: [...knownItems, ...newItems] }
      }
      return { visible, order, items: knownItems }
    }

    // 2) Legacy-Migration: positions vorhanden → in items[] konvertieren
    if (saved.positions) {
      const items = legacyLayoutToItems(visible, saved.positions)
        .map(it => it.i === 'welcome' ? { ...it, h: Math.max(it.h, WIDGET_HEIGHTS.welcome) } : it)
      const missingIds = visible.filter(id => !items.find(it => it.i === id))
      if (missingIds.length > 0) {
        const maxY = items.reduce((m, it) => Math.max(m, it.y + it.h), 0)
        let col = 0
        for (const id of missingIds) {
          const def = WIDGET_DEFS.find(d => d.id === id)!
          const w = def.defaultSpan as number
          if (col + w > GRID_COLS) col = 0
          items.push({ i: id, x: col, y: maxY, w, h: WIDGET_HEIGHTS[id] })
          col += w
        }
      }
      return { visible, order, items }
    }
  }

  // 3) Komplett neu (keine gespeicherten Daten)
  const visible = WIDGET_DEFS.filter(w => w.defaultVisible).map(w => w.id)
  const order = WIDGET_DEFS.map(w => w.id)
  return { visible, order, items: packItems(visible, order) }
}

function defaultLayout(): LayoutState {
  return parseLayout(null)
}

function saveLayoutToApi(layout: LayoutState) {
  fetch('/api/user/preferences/layout', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(layout),
  }).catch(() => {})
}

// ═══════════════════════════════════════════════════════════════
// Hauptkomponente
// ═══════════════════════════════════════════════════════════════

export default function FirmaOverview({ firma, primaryContact, teilnehmer, accountId, onShowTeilnehmer, onShowDualMatch, fomAnsprechpartner }: Props) {
  const { user } = useAuth()
  const { canRead } = useFirmaContext()
  const aktiveCount = firma.studierende_gesamt || teilnehmer.length
  const [layout, setLayout] = useState(defaultLayout)
  const [showSettings, setShowSettings] = useState(false)
  const favorites = useFavorites()

  // Einstellungen von API laden
  useEffect(() => {
    fetch('/api/user/preferences')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.dashboard_layout) setLayout(parseLayout(data.dashboard_layout))
        if (data?.favorite_teilnehmer) favorites.setInitial(data.favorite_teilnehmer)
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hauptAp = primaryContact
    ? { fullname: primaryContact.fullname, email: primaryContact.emailaddress1, telefon: primaryContact.telephone1, position: primaryContact.jobtitle, funktion: null, name: primaryContact.fullname }
    : null

  const updateLayout = (l: typeof layout) => { setLayout(l); saveLayoutToApi(l) }

  const getItem = useCallback((id: WidgetId): LayoutItem | undefined => {
    return layout.items?.find(it => it.i === id)
  }, [layout.items])

  const getSpan = useCallback((id: WidgetId): WidgetSpan => {
    const it = getItem(id)
    if (it) return Math.min(3, Math.max(1, it.w)) as WidgetSpan
    return (WIDGET_DEFS.find(w => w.id === id)?.defaultSpan ?? 1) as WidgetSpan
  }, [getItem])

  const toggleWidget = (id: WidgetId) => {
    const isVisible = layout.visible.includes(id)
    const items = layout.items ? [...layout.items] : []
    if (isVisible) {
      const visible = layout.visible.filter(v => v !== id)
      const newItems = items.filter(it => it.i !== id)
      updateLayout({ ...layout, visible, items: newItems })
    } else {
      const visible = [...layout.visible, id]
      const def = WIDGET_DEFS.find(d => d.id === id)!
      const w = def.defaultSpan as number
      const h = WIDGET_HEIGHTS[id]
      const maxY = items.reduce((m, it) => Math.max(m, it.y + it.h), 0)
      const newItems = [...items, { i: id, x: 0, y: maxY, w, h }]
      updateLayout({ ...layout, visible, items: newItems })
    }
  }

  const resetLayout = () => {
    const visible = WIDGET_DEFS.filter(w => w.defaultVisible).map(w => w.id)
    const order = WIDGET_DEFS.map(w => w.id)
    const fresh: LayoutState = { visible, order, items: packItems(visible, order) }
    updateLayout(fresh)
  }

  // Widgets die eine Section-Permission verlangen, werden ohne Berechtigung
  // ausgeblendet (kein Stub mit "keine Daten" — das wirkt wie ein Bug).
  const widgetSection: Partial<Record<WidgetId, string>> = {
    studierende: 'studiengaenge',
    vorlesungen: 'vorlesungstermine',
    noten: 'noten',
    dualmatch: 'studiengaenge',
  }
  const isWidgetAllowed = (id: WidgetId) => {
    const sec = widgetSection[id]
    return sec ? canRead(sec) : true
  }

  // Sichtbare Widgets, gefiltert auf erlaubte
  const visibleWidgets = useMemo(
    () => layout.visible.filter(isWidgetAllowed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout.visible, canRead],
  )

  // rgl-Layout aus items[] erzeugen, fehlende Items mit Default-Position auffüllen
  const rglLayout: RGLItem[] = useMemo(() => {
    const items = layout.items ?? []
    const out: RGLItem[] = []
    let fallbackY = items.reduce((m, it) => Math.max(m, it.y + it.h), 0)
    for (const id of visibleWidgets) {
      const it = items.find(x => x.i === id)
      const c = WIDGET_CONSTRAINTS[id]
      if (it) {
        out.push({ i: id, x: it.x, y: it.y, w: it.w, h: it.h, minW: c.minW, minH: c.minH, maxW: c.maxW })
      } else {
        const def = WIDGET_DEFS.find(d => d.id === id)!
        out.push({ i: id, x: 0, y: fallbackY, w: def.defaultSpan, h: WIDGET_HEIGHTS[id], minW: c.minW, minH: c.minH, maxW: c.maxW })
        fallbackY += WIDGET_HEIGHTS[id]
      }
    }
    return out
  }, [layout.items, visibleWidgets])

  // Debounce für Persistenz: nicht jeden Drag-Tick zur API durchreichen
  const persistTimer = useRef<number | null>(null)
  const handleLayoutChange = useCallback((next: readonly RGLItem[]) => {
    const items: LayoutItem[] = next.map(l => ({ i: l.i as WidgetId, x: l.x, y: l.y, w: l.w, h: l.h }))
    setLayout(prev => ({ ...prev, items }))
    if (persistTimer.current) window.clearTimeout(persistTimer.current)
    persistTimer.current = window.setTimeout(() => {
      setLayout(prev => { saveLayoutToApi(prev); return prev })
    }, 500)
  }, [])

  useEffect(() => () => {
    if (persistTimer.current) window.clearTimeout(persistTimer.current)
  }, [])

  const renderWidget = (id: WidgetId) => {
    switch (id) {
      case 'welcome': return <WelcomeWidget firma={firma} user={user} />
      case 'studierende': return <StudierendeWidget count={aktiveCount} onShow={() => onShowTeilnehmer?.()} teilnehmer={teilnehmer} favorites={favorites} span={getSpan('studierende')} accountId={accountId} />
      case 'ansprechpartner': return <AnsprechpartnerWidget fomAps={fomAnsprechpartner ?? []} accountId={accountId} />
      case 'quicklinks': return <QuicklinksWidget />
      case 'blackboard': return <BlackboardWidget accountId={accountId} />
      case 'vorlesungen': return <VorlesungenWidget accountId={accountId} teilnehmer={teilnehmer} />
      case 'noten': return <NotenWidget accountId={accountId} teilnehmer={teilnehmer} />
      case 'dualmatch': return <DualMatchWidget onOpen={() => onShowDualMatch?.()} />
      case 'events': return <EventsWidget firma={firma} hauptAp={hauptAp} user={user} />
      case 'news': return <NewsWidget />
      case 'magazine': return <MagazineWidget />
      default: return null
    }
  }

  return (
    <div className="-mx-6 -mt-2 overflow-y-auto">
      {/* ══ Hero ══ */}
      <div className="relative h-[420px] overflow-hidden">
        <img src={assetUrl('/login-bg.jpg')} alt="FOM Partnerportal" className="absolute inset-0 w-full h-full object-cover object-[center_30%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
          <h1 className="text-white text-4xl md:text-5xl font-display font-black tracking-tight drop-shadow-lg">
            FOM Partnerportal
          </h1>
          <p className="text-white/70 text-base mt-3">Alles für Ihre Zusammenarbeit mit der FOM Hochschule</p>
          <p className="text-white/60 text-sm mt-1">Alle relevanten Informationen und Services an einem Ort.</p>
        </div>
      </div>

      {/* ══ Toolbar ══ */}
      <div className="px-8 lg:px-16 pt-6 pb-2 flex justify-end">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
            showSettings ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Settings size={14} />
          Dashboard anpassen
        </button>
      </div>

      {/* ══ Widget Editor Panel ══ */}
      {showSettings && (
        <div className="px-8 lg:px-16 pb-6">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div>
                <h3 className="text-base font-display font-black text-gray-900">Widget-Editor</h3>
                <p className="text-xs text-gray-400 mt-0.5">Widgets ein-/ausblenden. Position direkt per Drag &amp; Drop anpassen, Größe per Resize-Handle an den Widget-Ecken.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={resetLayout} className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  Zurücksetzen
                </button>
                <button onClick={() => setShowSettings(false)} className="px-3 py-1.5 text-xs font-semibold text-white bg-fom-600 rounded-lg hover:bg-fom-700 transition-colors">
                  Fertig
                </button>
              </div>
            </div>

            {/* Widget-Liste (nur ein-/ausblenden — Größe per Resize-Handle direkt im Dashboard) */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {layout.order.map(id => {
                const def = WIDGET_DEFS.find(w => w.id === id)!
                const active = layout.visible.includes(id)
                return (
                  <div
                    key={id}
                    className={`
                      relative rounded-xl border-2 p-4 transition-all select-none
                      ${active ? 'border-fom-300 bg-fom-50/50' : 'border-gray-200 bg-gray-50/50 opacity-60'}
                    `}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xl">{def.icon}</span>
                      <button
                        onClick={() => toggleWidget(id)}
                        className={`relative rounded-full transition-colors ${active ? 'bg-fom-500' : 'bg-gray-300'}`}
                        style={{ width: 40, height: 22, flexShrink: 0 }}
                      >
                        <span
                          className="absolute top-0.5 rounded-full bg-white shadow transition-transform"
                          style={{ width: 18, height: 18, left: active ? 20 : 2 }}
                        />
                      </button>
                    </div>
                    <div className="font-semibold text-sm text-gray-900 leading-tight">{def.label}</div>
                    <div className="text-xs text-gray-400 mt-1 leading-relaxed">{def.description}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══ Widget Grid (react-grid-layout) ══ */}
      <div className={`px-8 lg:px-16 pb-8 ${showSettings ? 'rgl-edit-mode' : ''}`}>
        <ResponsiveGridLayout
          className="layout"
          layout={rglLayout}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          margin={[20, 20]}
          containerPadding={[0, 0]}
          isDraggable={showSettings}
          isResizable={showSettings}
          compactType="vertical"
          preventCollision={false}
          draggableCancel="button, a, select, input, textarea, .rgl-no-drag"
          onLayoutChange={handleLayoutChange}
        >
          {visibleWidgets.map(id => {
            const def = WIDGET_DEFS.find(w => w.id === id)!
            return (
              <div key={id} className="rgl-cell">
                <WidgetShell
                  label={def.label}
                  icon={def.icon}
                  onClose={() => toggleWidget(id)}
                  dragging={showSettings}
                  hideTitle={id === 'welcome' || id === 'studierende' || id === 'ansprechpartner' || id === 'dualmatch'}
                >
                  {renderWidget(id)}
                </WidgetShell>
              </div>
            )
          })}
        </ResponsiveGridLayout>
      </div>

      {/* ══ Footer ══ */}
      <Footer />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Widget Shell
// ═══════════════════════════════════════════════════════════════

function WidgetShell({
  label, icon, children, onClose, dragging, hideTitle,
}: {
  label: string
  icon: string
  children: ReactNode
  onClose: () => void
  dragging?: boolean
  hideTitle?: boolean
}) {
  return (
    <div className="relative h-full bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-card hover:shadow-md transition-shadow flex flex-col">
      {/* Titel-Leiste: immer anzeigen (außer hideTitle), im Drag-Modus mit Grip-Icon */}
      {!hideTitle && (
        <div className={`flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0 ${dragging ? 'bg-gray-50/80 cursor-grab select-none' : 'bg-white'}`}>
          <div className="flex items-center gap-2 text-sm">
            {dragging && <GripVertical size={14} className="text-gray-300" />}
            <span>{icon}</span>
            <span className="font-semibold text-gray-700">{label}</span>
          </div>
          {dragging && (
            <button
              onClick={onClose}
              className="rgl-no-drag text-gray-400 hover:text-red-500 p-1"
              title="Ausblenden"
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}
      {/* Floating Edit-Toolbar fuer Widgets ohne Titel-Leiste */}
      {hideTitle && dragging && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
          <button
            onClick={onClose}
            className="rgl-no-drag bg-white border border-gray-200 shadow-sm rounded-lg p-1.5 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
            title="Ausblenden"
            type="button"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {/* Edit-Mode Outline um das Widget herum */}
      {dragging && (
        <div className="absolute inset-0 pointer-events-none rounded-2xl border-2 border-dashed border-fom-300/60 z-10" />
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        {children}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Widgets
// ═══════════════════════════════════════════════════════════════

function WelcomeWidget({ firma, user }: { firma: Firma; user: any }) {
  return (
    <div className="p-8 flex items-start gap-6">
      <div className="shrink-0 w-20 h-20 rounded-xl overflow-hidden">
        {firma.logo ? (
          <img src={firma.logo} alt={firma.name} className="w-20 h-20 rounded-xl object-contain bg-white border border-gray-100 shadow-sm p-2" />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-fom-50 border border-fom-100 flex items-center justify-center text-fom-600 font-bold text-2xl">
            {firma.name?.charAt(0)?.toUpperCase() || 'F'}
          </div>
        )}
      </div>
      <div className="min-w-0 pt-1">
        <span className="text-fom-500 text-xs font-bold uppercase tracking-[0.15em]">Partnerportal · {firma.name}</span>
        <h2 className="text-2xl font-display font-black text-gray-900 mt-1 leading-tight">
          Herzlich Willkommen, {user?.DisplayName ?? ''}.
        </h2>
        <p className="text-gray-500 text-sm leading-relaxed mt-2 max-w-[480px]">
          Hier können Sie Ihre Studierenden verwalten, neue Talente finden und alle wichtigen Informationen einsehen.
        </p>
      </div>
    </div>
  )
}

function StudentAvatar({
  accountId, personenId, vorname, nachname, size,
}: {
  accountId: string
  personenId: number
  vorname: string | null | undefined
  nachname: string | null | undefined
  size: number
}) {
  return (
    <Avatar
      photoUrl={`/api/firma/${accountId}/teilnehmer/${personenId}/photo`}
      fallbackName={`${vorname ?? ''} ${nachname ?? ''}`.trim()}
      size={size}
    />
  )
}

function StudierendeWidget({
  count, onShow, teilnehmer, favorites, span = 1, accountId,
}: {
  count: number
  onShow: () => void
  teilnehmer: Teilnehmer[]
  favorites: ReturnType<typeof useFavorites>
  span?: WidgetSpan
  accountId: string
}) {
  const { firmaToken } = useParams<{ firmaToken: string }>()
  const favTn = teilnehmer.filter(t => favorites.isFav(t.StudiengangTnId))
  const showList = span >= 2
  // Sortierung: Favoriten zuerst, dann alphabetisch
  const sortedTn = useMemo(() => {
    return [...teilnehmer].sort((a, b) => {
      const fa = favorites.isFav(a.StudiengangTnId) ? 0 : 1
      const fb = favorites.isFav(b.StudiengangTnId) ? 0 : 1
      if (fa !== fb) return fa - fb
      return `${a.Nachname} ${a.Vorname}`.localeCompare(`${b.Nachname} ${b.Vorname}`)
    })
  }, [teilnehmer, favorites])
  // Bei Mittel ca. 6, bei Groß ca. 12 Eintraege
  const listLimit = span === 3 ? 12 : 6

  return (
    <div className="h-full flex flex-col rounded-2xl p-8 text-left" style={{ backgroundColor: '#0B2623' }}>
      {showList ? (
        // ── Mittel/Groß: Zahl oben + Liste rechts/unten ──
        <div className="flex flex-col h-full">
          <div className="flex items-baseline justify-between mb-1">
            <button onClick={onShow} className="text-left border-none bg-transparent cursor-pointer p-0 group">
              <div className="text-fom-400 text-[3.5rem] font-black leading-none tabular-nums">{count}</div>
              <div className="text-white/90 text-sm font-semibold mt-1 leading-snug">Studierende in Ihrem Unternehmen</div>
            </button>
            <button
              onClick={onShow}
              className="text-fom-400 text-xs font-semibold hover:text-fom-300 inline-flex items-center gap-1 group transition-all bg-transparent border-none cursor-pointer"
            >
              Alle anzeigen <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          <div className="mt-5 pt-5 border-t border-white/10 flex-1 min-h-0 overflow-y-auto pr-1">
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-3">
              Studierendenliste
            </div>
            <div className={`grid gap-2 ${span === 3 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {sortedTn.slice(0, listLimit).map(tn => {
                const isFav = favorites.isFav(tn.StudiengangTnId)
                return (
                  <Link
                    key={tn.StudiengangTnId}
                    to={`/firma/${firmaToken}/teilnehmer/${tn.PersonenFID}`}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors no-underline group"
                  >
                    <StudentAvatar
                      accountId={accountId}
                      personenId={tn.PersonenFID}
                      vorname={tn.Vorname}
                      nachname={tn.Nachname}
                      size={28}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-white/90 font-medium truncate group-hover:text-white">
                        {tn.Vorname} {tn.Nachname}
                      </div>
                      {tn.Studiengang && (
                        <div className="text-[10px] text-white/40 truncate">{tn.Studiengang}</div>
                      )}
                    </div>
                    <button
                      onClick={e => { e.preventDefault(); e.stopPropagation(); favorites.toggle(tn.StudiengangTnId) }}
                      className={`shrink-0 transition-colors bg-transparent border-none cursor-pointer ${isFav ? 'text-yellow-400 hover:text-yellow-300' : 'text-white/20 hover:text-white/40'}`}
                      title={isFav ? 'Aus Favoriten entfernen' : 'Als Favorit markieren'}
                    >
                      <Star size={12} fill={isFav ? 'currentColor' : 'none'} />
                    </button>
                  </Link>
                )
              })}
            </div>
            {sortedTn.length > listLimit && (
              <button
                onClick={onShow}
                className="mt-3 text-[10px] text-white/40 font-semibold hover:text-white/60 bg-transparent border-none cursor-pointer p-0"
              >
                +{sortedTn.length - listLimit} weitere anzeigen
              </button>
            )}
          </div>
        </div>
      ) : (
        // ── Klein: nur Zahl + Favoriten ──
        <>
          <button onClick={onShow} className="flex-1 flex flex-col justify-end text-left border-none cursor-pointer group transition-all duration-200 hover:scale-[1.01] bg-transparent p-0">
            <span className="text-fom-400 text-[3.5rem] font-black leading-none tabular-nums">{count}</span>
            <span className="text-white/90 text-sm font-semibold mt-2 leading-snug">Studierende in<br />Ihrem Unternehmen</span>
            <span className="inline-flex items-center gap-1 text-fom-400 text-xs font-semibold mt-3 group-hover:gap-2 transition-all">Alle anzeigen <ChevronRight size={12} /></span>
          </button>
          {favTn.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Star size={10} className="text-yellow-400" /> Favoriten
              </div>
              <div className="space-y-1.5">
                {favTn.slice(0, 3).map(tn => (
                  <div key={tn.StudiengangTnId} className="flex items-center gap-2">
                    <StudentAvatar
                      accountId={accountId}
                      personenId={tn.PersonenFID}
                      vorname={tn.Vorname}
                      nachname={tn.Nachname}
                      size={24}
                    />
                    <span className="text-xs text-white/80 font-medium truncate">{tn.Vorname} {tn.Nachname}</span>
                    <button onClick={e => { e.stopPropagation(); favorites.toggle(tn.StudiengangTnId) }} className="ml-auto text-yellow-400 hover:text-yellow-300 shrink-0"><Star size={11} fill="currentColor" /></button>
                  </div>
                ))}
                {favTn.length > 3 && <div className="text-[10px] text-white/30 font-semibold">+{favTn.length - 3} weitere</div>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function AnsprechpartnerWidget({ fomAps, accountId }: { fomAps: FomAnsprechpartner[]; accountId: string }) {
  // Account-Manager-Slot ans Ende sortieren, damit Geschäftsleitung/Team oben steht
  const display = [...fomAps]
    .sort((a, b) => (a.role === 'account_manager' ? 1 : 0) - (b.role === 'account_manager' ? 1 : 0))
    .slice(0, 2)
  if (display.length === 0) {
    return (
      <div className="p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Ihre Ansprechpartner bei der FOM</h3>
        <div className="text-sm text-gray-400">Kein FOM-Ansprechpartner zugewiesen</div>
      </div>
    )
  }
  return (
    <div className="p-6 flex flex-col gap-5">
      <h3 className="text-sm font-bold text-gray-900">Ihre Ansprechpartner bei der FOM</h3>
      {display.map(ap => <FomApCard key={ap.systemuser_id} ap={ap} accountId={accountId} />)}
    </div>
  )
}

function FomApCard({ ap, accountId }: { ap: FomAnsprechpartner; accountId: string }) {
  const photoUrl = ap.personen_id != null
    ? api.fomAnsprechpartnerPhotoUrl(accountId, ap.personen_id)
    : null
  return (
    <div className="flex items-center gap-4">
      <Avatar
        photoUrl={photoUrl}
        fallbackName={`${ap.vorname ?? ''} ${ap.nachname ?? ''}`.trim() || ap.fullname}
        size={56}
      />
      <div className="min-w-0 flex-1">
        <div className="text-base font-display font-bold text-gray-900 truncate">
          {ap.fullname}
        </div>
        {ap.position && (
          <div className="text-xs text-gray-500 mt-0.5 truncate">{ap.position}</div>
        )}
        {ap.email && (
          <a
            href={`mailto:${ap.email}`}
            className="inline-flex items-center gap-1 text-xs text-fom-600 hover:text-fom-700 font-medium no-underline mt-2"
          >
            <Mail size={12} /> {ap.email}
          </a>
        )}
        {ap.telefon && (
          <a
            href={`tel:${ap.telefon.replace(/[^\d+]/g, '')}`}
            className="flex items-center gap-1 text-xs text-fom-600 hover:text-fom-700 font-medium no-underline mt-1"
          >
            <Phone size={12} /> {ap.telefon}
          </a>
        )}
      </div>
    </div>
  )
}

function QuicklinksWidget() {
  const navigate = useNavigate()
  const { firmaToken } = useParams<{ firmaToken: string }>()
  const { canRead } = useFirmaContext()
  // section: null = immer sichtbar; sonst: nur wenn canRead(section).
  const links: { label: string; desc: string; icon: string; hash?: string; path?: string; section: string | null }[] = [
    { label: 'Ihr Unternehmen', desc: 'Stammdaten verwalten', icon: '🏢', hash: '#stammdaten', section: 'stammdaten' },
    { label: 'Ihre Teilnehmer', desc: 'Studierende verwalten', icon: '🎓', hash: '#teilnehmer', section: 'studiengaenge' },
    { label: 'Rechnungen', desc: 'Rechnungen und Zahlungen', icon: '📄', hash: '#rechnungen', section: 'rechnungen' },
    { label: 'Ansprechpartner', desc: 'Kontaktpersonen verwalten', icon: '👥', hash: '#ansprechpartner', section: 'ansprechpartner' },
  ]
  const visibleLinks = links.filter(l => l.section === null || canRead(l.section))
  if (visibleLinks.length === 0) return null
  return (
    <div className="p-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {visibleLinks.map(l => (
          <button key={l.label} onClick={() => {
            // Konsistent zu Tab-Bar + FirmaNavbar-Links: jeder Klick fuegt
            // einen eigenen History-Eintrag hinzu (kein replace). Damit
            // navigiert Browser-Back Station-fuer-Station zurueck.
            navigate(l.path || `/firma/${firmaToken}${l.hash}`)
          }}
            className="flex flex-col items-center gap-2 p-5 rounded-xl bg-gray-50/80 hover:bg-fom-50 hover:border-fom-200 border border-transparent transition-all cursor-pointer group text-center">
            <span className="text-2xl">{l.icon}</span>
            <span className="text-sm font-semibold text-gray-900 group-hover:text-fom-600">{l.label}</span>
            <span className="text-xs text-gray-400">{l.desc}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function DualMatchWidget({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="relative overflow-hidden group cursor-pointer" style={{ height: '280px' }} onClick={onOpen}>
      <img src={assetUrl('/FOM_Dual Match_Key-Visual_TeaserTile_B2B.png')} alt="FOM DualMatch"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        style={{ objectPosition: 'center 30%' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end p-8">
        <h3 className="text-white text-2xl font-display font-black leading-tight">FOM DualMatch</h3>
        <p className="text-white/75 text-base mt-1">Passende Auszubildende für Ihr Unternehmen finden</p>
        <span className="inline-flex items-center gap-1 text-fom-400 text-sm font-semibold mt-3 group-hover:gap-2 transition-all">
          DualMatch öffnen <ChevronRight size={14} />
        </span>
      </div>
    </div>
  )
}

// ── Vorlesungen Widget ───────────────────────────────────────

const VORLESUNG_COLORS = [
  { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', dot: '#3b82f6' },
  { bg: '#f5f3ff', border: '#c4b5fd', text: '#5b21b6', dot: '#8b5cf6' },
  { bg: '#ecfdf5', border: '#6ee7b7', text: '#065f46', dot: '#10b981' },
  { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', dot: '#f59e0b' },
  { bg: '#fdf2f8', border: '#f9a8d4', text: '#9d174d', dot: '#ec4899' },
  { bg: '#ecfeff', border: '#67e8f9', text: '#155e75', dot: '#06b6d4' },
]

function VorlesungenWidget({ accountId, teilnehmer }: { accountId: string; teilnehmer: Teilnehmer[] }) {
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [expandedBooking, setExpandedBooking] = useState<number | null>(null)
  const [showCal, setShowCal] = useState(false)
  const [calMonth, setCalMonth] = useState(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))

  // WICHTIG: lokales Datum, NICHT toISOString() — sonst Off-by-one bei MEZ/MESZ
  // (lokal 27.5. 00:00 → UTC 26.5. 22:00 → ISO "2026-05-26"). Bug 2026-05-26 gefixt.
  const dateISO = useMemo(() => {
    const y = selectedDate.getFullYear()
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0')
    const d = String(selectedDate.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }, [selectedDate])
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d }, [])

  // 7-Tage-Leiste zentriert um selectedDate (3 vorher, selected, 3 nachher)
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(selectedDate)
      d.setDate(d.getDate() + i - 3)
      d.setHours(0,0,0,0)
      return d
    }), [selectedDate])

  const shiftWeek = (dir: number) => {
    setSelectedDate(prev => { const d = new Date(prev); d.setDate(d.getDate() + dir * 7); return d })
  }
  const pickDate = (d: Date) => { setSelectedDate(d); setShowCal(false) }
  const goToday = () => setSelectedDate(new Date())

  // Kalender-Tage
  const calDays = useMemo(() => {
    const year = calMonth.getFullYear(), month = calMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const startOffset = firstDay === 0 ? 6 : firstDay - 1
    const days: (number | null)[] = Array(startOffset).fill(null)
    for (let i = 1; i <= daysInMonth; i++) days.push(i)
    while (days.length % 7 !== 0) days.push(null)
    return days
  }, [calMonth])

  const { data, isLoading } = useQuery({
    queryKey: ['vorlesungen-widget', accountId, dateISO],
    queryFn: () => api.termineHeute(accountId, dateISO),
    staleTime: 5 * 60 * 1000,
  })

  const termine = data?.termine ?? []

  const grouped = useMemo(() => {
    const map = new Map<number, typeof termine>()
    for (const t of termine) {
      const list = map.get(t.BuchungsID) ?? []
      list.push(t)
      map.set(t.BuchungsID, list)
    }
    return [...map.entries()]
      .map(([id, items]) => ({ id, first: items[0], students: items, count: items.length }))
      .sort((a, b) => (a.first.Von ?? '').localeCompare(b.first.Von ?? ''))
  }, [termine])

  const dateLabel = selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' })
  const isToday = selectedDate.toDateString() === today.toDateString()

  return (
    <div className="p-5">
      {/* Tagesauswahl mit Navigation */}
      <div className="flex items-center gap-1 mb-4">
        <button onClick={() => shiftWeek(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0"><ChevronLeft size={16} /></button>
        <div className="flex-1 flex items-center gap-1 overflow-x-auto pb-0.5 justify-center">
          {weekDays.map((d, i) => {
            const active = d.toDateString() === selectedDate.toDateString()
            const dayIsToday = d.toDateString() === today.toDateString()
            return (
              <button
                key={i}
                onClick={() => setSelectedDate(new Date(d))}
                className={`flex flex-col items-center px-3 py-2 rounded-xl text-center transition-all shrink-0 min-w-[52px] ${
                  active ? 'bg-fom-500 text-white shadow-md' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  {d.toLocaleDateString('de-DE', { weekday: 'short' })}
                </span>
                <span className={`text-lg font-black leading-tight ${active ? '' : 'text-gray-900'}`}>
                  {d.getDate()}
                </span>
                {dayIsToday && <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${active ? 'bg-white' : 'bg-fom-400'}`} />}
              </button>
            )
          })}
        </div>
        <button onClick={() => shiftWeek(1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0"><ChevronRight size={16} /></button>
        {/* Kalender-Button */}
        <div className="relative shrink-0 ml-1">
          <button
            onClick={() => { setCalMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)); setShowCal(!showCal) }}
            className={`p-2 rounded-xl border transition-all ${showCal ? 'bg-fom-50 border-fom-400 text-fom-600' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600'}`}
            title="Datum wählen"
          >
            <CalendarDays size={16} />
          </button>
          {/* Kalender-Dropdown */}
          {showCal && (
            <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 w-[280px]">
              {/* Monat-Navigation */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronLeft size={14} /></button>
                <span className="text-sm font-bold text-gray-900">{calMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</span>
                <button onClick={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronRight size={14} /></button>
              </div>
              {/* Kalender-Grid */}
              <div className="grid grid-cols-7 gap-px">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                  <div key={d} className="text-center py-1 text-[9px] font-bold text-gray-400 uppercase">{d}</div>
                ))}
                {calDays.map((day, i) => {
                  if (day == null) return <div key={i} />
                  const d = new Date(calMonth.getFullYear(), calMonth.getMonth(), day)
                  const isSel = d.toDateString() === selectedDate.toDateString()
                  const isTod = d.toDateString() === today.toDateString()
                  return (
                    <button
                      key={i}
                      onClick={() => pickDate(d)}
                      className={`py-1.5 text-sm rounded-lg transition-all ${
                        isSel ? 'bg-fom-500 text-white font-black' :
                        isTod ? 'bg-fom-50 text-fom-600 font-bold' :
                        'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {day}
                    </button>
                  )
                })}
              </div>
              {/* Heute-Link */}
              <div className="mt-2 pt-2 border-t border-gray-100 text-center">
                <button onClick={() => { goToday(); setShowCal(false) }} className="text-xs text-fom-500 font-semibold hover:text-fom-700">Heute</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700">{dateLabel}</span>
          {!isToday && <button onClick={goToday} className="text-[10px] text-fom-500 font-semibold hover:text-fom-700">Heute</button>}
        </div>
        <span className="text-xs text-gray-400">{grouped.length} Vorlesung{grouped.length !== 1 ? 'en' : ''}</span>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      )}

      {/* Keine Vorlesungen */}
      {!isLoading && grouped.length === 0 && (
        <div className="text-center py-8">
          <div className="text-3xl mb-2">🎉</div>
          <p className="text-sm font-semibold text-gray-900">Keine Vorlesungen</p>
          <p className="text-xs text-gray-400 mt-1">
            {isToday ? 'Alle Studierenden sind heute verfügbar.' : `Am ${dateLabel} keine Vorlesungen geplant.`}
          </p>
        </div>
      )}

      {/* Vorlesungsliste */}
      {!isLoading && grouped.length > 0 && (
        <div className="space-y-2">
          {grouped.map((g, gIdx) => {
            const color = VORLESUNG_COLORS[gIdx % VORLESUNG_COLORS.length]
            const expanded = expandedBooking === g.id
            const cancelled = g.first.Stattgefunden === 'N'
            return (
              <div key={g.id}>
                <button
                  onClick={() => setExpandedBooking(expanded ? null : g.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                    expanded ? 'shadow-md' : 'hover:shadow-sm'
                  } ${cancelled ? 'opacity-50' : ''}`}
                  style={{ backgroundColor: color.bg, borderColor: color.border }}
                >
                  {/* Zeit */}
                  <div className="shrink-0 text-center w-14">
                    <div className="text-sm font-black tabular-nums" style={{ color: color.text }}>
                      {g.first.Von?.slice(0, 5) ?? '–'}
                    </div>
                    <div className="text-[10px] tabular-nums" style={{ color: color.text, opacity: 0.6 }}>
                      {g.first.Bis?.slice(0, 5) ?? ''}
                    </div>
                  </div>
                  {/* Trennlinie */}
                  <div className="w-0.5 h-8 rounded-full shrink-0" style={{ backgroundColor: color.dot }} />
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate" style={{ color: color.text }}>
                      {g.first.Fach ?? '–'}
                      {cancelled && <span className="ml-2 text-red-500 text-[10px] font-bold uppercase">Ausgefallen</span>}
                    </div>
                    <div className="text-xs truncate" style={{ color: color.text, opacity: 0.6 }}>
                      {g.first.Dozent ?? ''} · {g.first.Lehrgang ?? ''}
                    </div>
                  </div>
                  {/* Studenten-Count */}
                  <div
                    className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ backgroundColor: color.dot, color: '#fff' }}
                  >
                    <Users size={11} />
                    {g.count}
                  </div>
                </button>

                {/* Expanded: Studenten-Liste */}
                {expanded && (
                  <div className="ml-4 mr-2 mt-1 mb-2 pl-4 border-l-2 space-y-1" style={{ borderColor: color.border }}>
                    {g.students.map((s, si) => {
                      const tn = teilnehmer.find(t => t.StudiengangTnId === s.StudiengangTnId)
                      return (
                        <div key={si} className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-white/60 text-sm">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ backgroundColor: color.dot }}>
                            {s.Vorname?.charAt(0)}{s.Nachname?.charAt(0)}
                          </div>
                          <span className="font-semibold text-gray-900">{s.Vorname} {s.Nachname}</span>
                          {tn?.Studiengang && (
                            <span className="text-xs text-gray-400 truncate ml-auto">{tn.Studiengang}</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}


// ── Noten Widget ─────────────────────────────────────────────

function NotenWidget({ accountId, teilnehmer }: { accountId: string; teilnehmer: Teilnehmer[] }) {
  const [showAll, setShowAll] = useState(false)
  const [filterStudent, setFilterStudent] = useState<number | ''>('')

  const { data: allNoten, isLoading } = useQuery({
    queryKey: ['noten-widget', accountId, teilnehmer.map(t => t.StudiengangTnId).join(',')],
    queryFn: async () => {
      const results = await Promise.all(
        teilnehmer.slice(0, 20).map(async tn => {
          try {
            const res = await api.noten(accountId, tn.StudiengangTnId)
            return res.noten.map(n => ({ ...n, _tnVorname: tn.Vorname, _tnNachname: tn.Nachname, _tnStudiengang: tn.Studiengang }))
          } catch { return [] }
        })
      )
      return results.flat()
    },
    enabled: teilnehmer.length > 0,
    staleTime: 10 * 60 * 1000,
  })

  const alleNoten = useMemo(() => {
    if (!allNoten) return []
    return allNoten
      .filter(n => n.NoteFach > 0)
      .reduce((acc, n) => {
        const key = `${n.FachID}-${n.PersonenFID}`
        if (!acc.find(x => `${x.FachID}-${x.PersonenFID}` === key)) acc.push(n)
        return acc
      }, [] as typeof allNoten)
      .sort((a, b) => b.TeilleistungsID - a.TeilleistungsID)
  }, [allNoten])

  const gefiltert = useMemo(() =>
    filterStudent ? alleNoten.filter(n => n.PersonenFID === filterStudent) : alleNoten
  , [alleNoten, filterStudent])

  // Studenten die Noten haben
  const studentenMitNoten = useMemo(() => {
    const ids = new Set(alleNoten.map(n => n.PersonenFID))
    return teilnehmer.filter(t => ids.has(t.PersonenFID))
  }, [alleNoten, teilnehmer])

  const noteColor = (note: number) => {
    if (note <= 1.5) return { bg: '#dcfce7', text: '#166534', border: '#86efac' }
    if (note <= 2.5) return { bg: '#ecfdf5', text: '#065f46', border: '#6ee7b7' }
    if (note <= 3.5) return { bg: '#fffbeb', text: '#92400e', border: '#fcd34d' }
    if (note <= 4.0) return { bg: '#fef3c7', text: '#92400e', border: '#fde68a' }
    return { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' }
  }

  const NotenRow = ({ n, i }: { n: typeof alleNoten[number]; i: number }) => {
    const c = noteColor(n.NoteFach)
    const bestanden = n.NoteFach > 0 && n.NoteFach <= 4.0
    return (
      <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50/80 hover:bg-gray-100 transition-colors">
        <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-black text-base border" style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
          {n.NoteFach.toFixed(1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">{n.Fach}</div>
          <div className="text-xs text-gray-500 truncate">
            {(n as any)._tnVorname} {(n as any)._tnNachname}
            {(n as any)._tnStudiengang && <span className="text-gray-400 ml-1">· {(n as any)._tnStudiengang}</span>}
          </div>
          {n.Prüfung && <div className="text-[10px] text-gray-400 truncate mt-0.5">Prüfung: {n.Prüfung}</div>}
        </div>
        <div className="shrink-0">
          {bestanden ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold"><Check size={10} /> Bestanden</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold"><X size={10} /> Nicht best.</span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="p-5">
      {isLoading && <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />)}</div>}

      {!isLoading && alleNoten.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm"><span className="text-2xl block mb-2">📊</span>Keine Noten vorhanden.</div>
      )}

      {!isLoading && alleNoten.length > 0 && (
        <>
          {/* Quick-Filter Studenten */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <button
              onClick={() => setFilterStudent('')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                !filterStudent ? 'bg-fom-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >Alle</button>
            {studentenMitNoten.map(t => {
              const active = filterStudent === t.PersonenFID
              const count = alleNoten.filter(n => n.PersonenFID === t.PersonenFID).length
              return (
                <button
                  key={t.PersonenFID}
                  onClick={() => setFilterStudent(active ? '' : t.PersonenFID)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                    active ? 'bg-fom-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {t.Vorname} {t.Nachname}
                  <span className={`px-1 rounded text-[9px] ${active ? 'bg-white/20' : 'bg-gray-200/80'}`}>{count}</span>
                </button>
              )
            })}
          </div>

          <div className="space-y-1.5">
            {gefiltert.slice(0, 5).map((n, i) => <NotenRow key={i} n={n} i={i} />)}
          </div>

          {gefiltert.length > 5 && (
            <div className="mt-3 text-center">
              <button
                onClick={() => setShowAll(true)}
                className="inline-flex items-center gap-1 text-sm font-semibold text-fom-500 hover:text-fom-700 transition-colors"
              >
                Alle anzeigen ({gefiltert.length})
                <ChevronRight size={13} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Vollbild-Modal ── */}
      {showAll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => { setShowAll(false); setFilterStudent('') }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-lg font-display font-black text-gray-900">Alle Noten</h3>
              <button onClick={() => { setShowAll(false); setFilterStudent('') }} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>

            {/* Filter */}
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50 shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <select
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:border-fom-400 outline-none bg-white appearance-none pr-8 font-medium"
                    value={filterStudent}
                    onChange={e => setFilterStudent(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Alle Studierenden</option>
                    {studentenMitNoten.map(t => (
                      <option key={t.PersonenFID} value={t.PersonenFID}>
                        {t.Vorname} {t.Nachname} — {alleNoten.filter(n => n.PersonenFID === t.PersonenFID).length} Noten
                      </option>
                    ))}
                  </select>
                  <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" />
                </div>
                <div className="text-sm text-gray-500 font-semibold tabular-nums shrink-0">
                  {gefiltert.length} Ergebnis{gefiltert.length !== 1 ? 'se' : ''}
                </div>
              </div>
              {/* Schnellfilter Studenten als Chips */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {studentenMitNoten.map(t => {
                  const active = filterStudent === t.PersonenFID
                  const count = alleNoten.filter(n => n.PersonenFID === t.PersonenFID).length
                  const avg = alleNoten.filter(n => n.PersonenFID === t.PersonenFID).reduce((s, n) => s + n.NoteFach, 0) / count
                  return (
                    <button
                      key={t.PersonenFID}
                      onClick={() => setFilterStudent(active ? '' : t.PersonenFID)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        active ? 'bg-fom-500 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-fom-300 hover:bg-fom-50'
                      }`}
                    >
                      <span className="w-5 h-5 rounded-full bg-current/10 flex items-center justify-center text-[9px] font-black"
                        style={{ backgroundColor: active ? 'rgba(255,255,255,0.2)' : undefined }}>
                        {t.Vorname?.charAt(0)}{t.Nachname?.charAt(0)}
                      </span>
                      {t.Vorname} {t.Nachname}
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${active ? 'bg-white/20' : 'bg-gray-100'}`}>
                        ø {avg.toFixed(1)}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Noten-Liste */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
              {gefiltert.map((n, i) => <NotenRow key={i} n={n} i={i} />)}
              {gefiltert.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">Keine Noten für diesen Filter.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Events / Sonderveranstaltungen Widget ────────────────────

// Alle Sonderveranstaltungen (Musterdaten — später: Dynamics CRM)
interface CrmEvent {
  id: string; title: string; type: string; format: string
  start: string; end: string; description: string; description_html: string | null
  location: string; capacity: number | null; image: string | null
}

// Gradient-Farben nach Event-Typ
const EVENT_TYPE_COLORS: Record<string, [string, string]> = {
  'Sonderveranstaltung': ['#0d9488', '#134e4a'],
  'Infoveranstaltung': ['#3b82f6', '#1e3a8a'],
  'Infoveranstaltung - Master': ['#6366f1', '#312e81'],
  'Infoveranstaltung - Weiterbildung': ['#8b5cf6', '#4c1d95'],
  'Infoveranstaltung - Outgoings': ['#06b6d4', '#164e63'],
  'Probevorlesung': ['#f59e0b', '#78350f'],
  'Meet your Campus': ['#10b981', '#065f46'],
  'Eröffnung': ['#ec4899', '#831843'],
  'Messe': ['#ef4444', '#7f1d1d'],
  'Verabschiedung': ['#d97706', '#451a03'],
}
function eventGradient(type: string): string {
  const [c1, c2] = EVENT_TYPE_COLORS[type] ?? ['#64748b', '#1e293b']
  return `linear-gradient(135deg, ${c1}, ${c2})`
}

function EventsWidget({ firma, hauptAp, user }: { firma: Firma; hauptAp: any; user: any }) {
  const apName = hauptAp?.fullname || hauptAp?.name || user?.DisplayName || '–'
  const apEmail = hauptAp?.email || user?.Email || user?.email || '–'
  const apTelefon = hauptAp?.telefon || '–'

  const [showCalendar, setShowCalendar] = useState(false)
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })
  const [selectedEvent, setSelectedEvent] = useState<CrmEvent | null>(null)
  const [anmeldeForm, setAnmeldeForm] = useState({ anmerkung: '' })
  const [angemeldet, setAngemeldet] = useState<Set<string>>(new Set())
  const [eventTypeFilter, setEventTypeFilter] = useState('Sonderveranstaltung')

  // Events aus Dynamics laden
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['crm-events'],
    queryFn: () => api.events(50),
    staleTime: 10 * 60 * 1000,
  })
  const allEvents = eventsData?.events ?? []
  const uniqueEventTypes = [...new Set(allEvents.map(e => e.type).filter(Boolean))].sort()
  const filteredEvents = eventTypeFilter ? allEvents.filter(e => e.type === eventTypeFilter) : allEvents
  const nextEvents = filteredEvents.slice(0, 3)

  const monthEvents = filteredEvents.filter(e => {
    const d = new Date(e.start)
    return d.getFullYear() === calMonth.getFullYear() && d.getMonth() === calMonth.getMonth()
  })

  const calDays = useMemo(() => {
    const year = calMonth.getFullYear(), month = calMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const startOffset = firstDay === 0 ? 6 : firstDay - 1
    const days: (number | null)[] = Array(startOffset).fill(null)
    for (let i = 1; i <= daysInMonth; i++) days.push(i)
    while (days.length % 7 !== 0) days.push(null)
    return days
  }, [calMonth])

  const eventDays = new Set(monthEvents.map(e => new Date(e.start).getDate()))

  const openEvent = (e: CrmEvent) => { setSelectedEvent(e); setAnmeldeForm({ anmerkung: '' }) }
  const handleAnmelden = () => {
    if (!selectedEvent) return
    setAngemeldet(prev => new Set([...prev, selectedEvent.id]))
    setSelectedEvent(null)
  }

  const EventCard = ({ e }: { e: CrmEvent }) => {
    const d = new Date(e.start)
    return (
      <div onClick={() => openEvent(e)} className="group relative rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all border border-gray-200">
        <div className="relative h-[140px] overflow-hidden">
          {e.image ? (
            <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full group-hover:scale-105 transition-transform duration-500" style={{ background: eventGradient(e.type) }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-lg px-2.5 py-1.5 shadow-sm">
            <div className="text-lg font-black text-fom-600 leading-none text-center">{d.getDate()}</div>
            <div className="text-[9px] font-bold text-fom-500 uppercase tracking-wider text-center">{d.toLocaleDateString('de-DE', { month: 'short' })}</div>
          </div>
          <div className="absolute top-3 right-3">
            {angemeldet.has(e.id) && <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold mr-1">Angemeldet</span>}
            <span className="px-2 py-0.5 rounded-full bg-fom-500 text-white text-[9px] font-bold uppercase tracking-wider">{e.type}</span>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-sm font-bold text-gray-900 leading-snug group-hover:text-fom-600 transition-colors line-clamp-1">{e.title}</h3>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{e.description || e.title}</p>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <CalendarDays size={11} />{e.format}{e.location ? ` · ${e.location}` : ''}
            </div>
            <span className="inline-flex items-center gap-1 text-fom-500 text-xs font-semibold group-hover:gap-1.5 transition-all">Details <ChevronRight size={11} /></span>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) return <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1, 2, 3].map(i => <div key={i} className="rounded-xl bg-gray-100 animate-pulse h-[180px]" />)}</div>

  // Wenn nichts zu zeigen: Filter-Dropdown trotzdem anzeigen, damit User
  // den Typ wechseln kann statt mit „Keine Events" zu stranden.
  const filterBar = (
    <div className="flex items-center gap-3 mb-4 flex-wrap">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Veranstaltungsart</label>
      <div className="relative">
        <select
          value={eventTypeFilter}
          onChange={ev => setEventTypeFilter(ev.target.value)}
          className="appearance-none pl-4 pr-9 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:border-fom-300 focus:border-fom-500 focus:ring-2 focus:ring-fom-100 outline-none cursor-pointer transition-colors"
        >
          <option value="">Alle</option>
          {uniqueEventTypes.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none" />
      </div>
      <span className="text-xs text-gray-400">{filteredEvents.length} {filteredEvents.length === 1 ? 'Veranstaltung' : 'Veranstaltungen'}</span>
    </div>
  )

  if (allEvents.length === 0) return (
    <div className="p-5">
      {filterBar}
      <div className="py-8 text-center text-gray-400 text-sm">Keine kommenden Events.</div>
    </div>
  )

  if (filteredEvents.length === 0) return (
    <div className="p-5">
      {filterBar}
      <div className="py-8 text-center text-gray-400 text-sm">Keine Events für diesen Filter.</div>
    </div>
  )

  return (
    <div className="p-5">
      {filterBar}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {nextEvents.map(e => <EventCard key={e.id} e={e} />)}
      </div>
      {filteredEvents.length > 3 && (
        <div className="mt-4 text-center">
          <button onClick={() => setShowCalendar(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-fom-50 text-fom-600 text-sm font-semibold hover:bg-fom-100 transition-colors border border-fom-200">
            <CalendarDays size={15} /> Alle Veranstaltungen anzeigen ({filteredEvents.length})
          </button>
        </div>
      )}

      {/* Event-Detail + Anmeldung */}
      {selectedEvent && (() => {
        const sd = new Date(selectedEvent.start)
        const ed = new Date(selectedEvent.end)
        const timeStr = `${sd.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} – ${ed.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setSelectedEvent(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="relative h-[200px] overflow-hidden">
                {selectedEvent.image ? (
                  <img src={selectedEvent.image} alt={selectedEvent.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full" style={{ background: eventGradient(selectedEvent.type) }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                <button onClick={() => setSelectedEvent(null)} className="absolute top-4 right-4 p-1.5 rounded-full bg-black/30 text-white hover:bg-black/50"><X size={18} /></button>
                <div className="absolute bottom-4 left-6 right-6">
                  <span className="px-2 py-0.5 rounded-full bg-fom-500 text-white text-[10px] font-bold uppercase tracking-wider">{selectedEvent.type}</span>
                  <h2 className="text-2xl font-display font-black text-white mt-2 leading-tight">{selectedEvent.title}</h2>
                </div>
              </div>
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <CalendarDays size={16} className="mx-auto text-fom-500 mb-1" />
                    <div className="text-xs font-bold text-gray-900">{sd.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <Clock size={16} className="mx-auto text-fom-500 mb-1" />
                    <div className="text-xs font-bold text-gray-900">{timeStr}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <Users size={16} className="mx-auto text-fom-500 mb-1" />
                    <div className="text-xs font-bold text-gray-900">{selectedEvent.format}{selectedEvent.location ? ` · ${selectedEvent.location}` : ''}</div>
                  </div>
                </div>
                {selectedEvent.description && <p className="text-sm text-gray-600 leading-relaxed mb-6">{selectedEvent.description}</p>}
                {angemeldet.has(selectedEvent.id) ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                    <Check size={24} className="mx-auto text-emerald-500 mb-2" />
                    <div className="text-sm font-bold text-emerald-700">Sie sind angemeldet!</div>
                    <div className="text-xs text-emerald-600 mt-1">Wir senden Ihnen eine Bestätigung per E-Mail.</div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Anmeldung</h3>
                    <div className="space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Unternehmen</label><div className="mt-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm font-semibold text-gray-900">{firma.name}</div></div>
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Ansprechpartner</label><div className="mt-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm font-semibold text-gray-900">{apName}</div></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">E-Mail</label><div className="mt-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm text-gray-700">{apEmail}</div></div>
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Telefon</label><div className="mt-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm text-gray-700">{apTelefon}</div></div>
                      </div>
                      <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Anmerkung (optional)</label>
                        <textarea className="mt-1 w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm resize-none focus:border-fom-400 focus:ring-1 focus:ring-fom-200 outline-none" rows={2} placeholder="z.B. Teilnehmeranzahl, besondere Wünsche..." value={anmeldeForm.anmerkung} onChange={ev => setAnmeldeForm(f => ({ ...f, anmerkung: ev.target.value }))} />
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button onClick={() => setSelectedEvent(null)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">Abbrechen</button>
                      <button onClick={handleAnmelden} className="flex-1 px-4 py-2.5 rounded-xl bg-fom-500 text-white text-sm font-semibold hover:bg-fom-600 shadow-md">Jetzt anmelden</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* Kalender-Modal */}
      {showCalendar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowCalendar(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-display font-black text-gray-900">Alle Veranstaltungen</h3>
              <button onClick={() => setShowCalendar(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 65px)' }}>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft size={18} /></button>
                <span className="text-base font-bold text-gray-900">{calMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</span>
                <button onClick={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRight size={18} /></button>
              </div>
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-xl overflow-hidden mb-6">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                  <div key={d} className="bg-gray-50 text-center py-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">{d}</div>
                ))}
                {calDays.map((day, i) => {
                  const hasEvent = day != null && eventDays.has(day)
                  const isToday = day != null && new Date().getDate() === day && calMonth.getMonth() === new Date().getMonth() && calMonth.getFullYear() === new Date().getFullYear()
                  return (
                    <div key={i} className={`bg-white py-2.5 text-center text-sm cursor-pointer hover:bg-fom-50 transition-colors ${day ? '' : 'bg-gray-50/50'} ${isToday ? 'font-black text-fom-600' : 'text-gray-700'}`}>
                      {day ?? ''}{hasEvent && <div className="w-1.5 h-1.5 rounded-full bg-fom-500 mx-auto mt-0.5" />}
                    </div>
                  )
                })}
              </div>
              {monthEvents.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Events in {calMonth.toLocaleDateString('de-DE', { month: 'long' })}</h4>
                  {monthEvents.map(e => {
                    const d = new Date(e.start)
                    return (
                      <div key={e.id} onClick={() => { setShowCalendar(false); openEvent(e) }} className="flex gap-4 p-4 rounded-xl border border-gray-200 hover:border-fom-200 hover:bg-fom-50/30 transition-all cursor-pointer">
                        <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                          {e.image ? <img src={e.image} alt={e.title} className="w-full h-full object-cover" />
                            : <div className="w-full h-full rounded-lg" style={{ background: eventGradient(e.type) }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-fom-500">{d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long' })}</span>
                            <span className="px-1.5 py-0.5 rounded bg-fom-100 text-fom-600 text-[9px] font-bold uppercase">{e.type}</span>
                            {angemeldet.has(e.id) && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-600 text-[9px] font-bold">Angemeldet</span>}
                          </div>
                          <h4 className="text-sm font-bold text-gray-900 mt-1">{e.title}</h4>
                          <div className="flex items-center gap-1 text-xs text-gray-400 mt-1"><CalendarDays size={10} />{e.format}{e.location ? ` · ${e.location}` : ''}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">Keine Veranstaltungen in diesem Monat.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function NewsWidget() {
  const { data, isLoading } = useQuery({ queryKey: ['fom-news'], queryFn: () => api.news(3), staleTime: 10 * 60 * 1000 })
  const articles = data?.articles ?? []

  if (isLoading) return <div className="p-5 grid grid-cols-3 gap-4">{[1, 2, 3].map(i => <div key={i} className="rounded-xl bg-gray-100 animate-pulse h-[240px]" />)}</div>
  if (!articles.length) return <div className="p-8 text-center text-gray-400 text-sm">Keine News verfügbar.</div>

  return (
    <div className="p-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {articles.map(a => (
          <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"
            className="group block bg-gray-50/80 rounded-xl overflow-hidden hover:shadow-md transition-all no-underline border border-transparent hover:border-fom-200">
            <div className="relative h-[140px] overflow-hidden bg-gray-100">
              {a.image ? <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                : <div className="w-full h-full flex items-center justify-center"><Newspaper size={28} className="text-gray-300" /></div>}
            </div>
            <div className="p-4">
              {a.published_at && <span className="text-fom-500 text-[10px] font-semibold uppercase tracking-wider">{new Date(a.published_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
              <h3 className="text-sm font-bold text-gray-900 leading-snug mt-1 line-clamp-2 group-hover:text-fom-600">{a.title}</h3>
              <span className="inline-flex items-center gap-1 text-fom-500 text-xs font-semibold mt-2 group-hover:gap-1.5 transition-all">Weiterlesen <ExternalLink size={11} /></span>
            </div>
          </a>
        ))}
      </div>
    </div>
  )
}

function MagazineWidget() {
  return (
    <div className="p-5 grid grid-cols-2 grid-rows-1 gap-4 h-full">
      {[{ title: 'FOM Talente-Magazin', desc: 'Inhalte für Unternehmen rund um Studium und Nachwuchs', img: assetUrl('/talente_mag_mockup_header.png'), cta: 'Talente-Magazin herunterladen', url: 'https://www.fom.de' },
        { title: 'FOM Online-Magazin', desc: 'Spannende Unternehmensgeschichten aus der FOM', img: assetUrl('/AdobeStock_614844122.jpeg'), cta: 'Online-Magazin lesen', url: 'https://www.fom.de/de/online-magazin.html' },
      ].map(m => (
        <a key={m.title} href={m.url} target="_blank" rel="noopener noreferrer"
          className="relative h-full min-h-0 overflow-hidden rounded-xl group cursor-pointer block no-underline">
          <img src={m.img} alt={m.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white text-xl font-display font-black drop-shadow-sm leading-tight">{m.title}</h3>
            <p className="text-white/85 text-sm mt-1 leading-snug">{m.desc}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-fom-700 shadow-sm group-hover:gap-2.5 transition-all">
              {m.cta}
              <ExternalLink size={13} />
            </span>
          </div>
        </a>
      ))}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Footer
// ═══════════════════════════════════════════════════════════════

function Footer() {
  return (
    <footer style={{ background: '#0b2623', padding: '64px 32px 32px', marginTop: 48 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto 16px' }}>
        <img src={assetUrl('/fom-logo-negativ.png')} alt="FOM Hochschule" style={{ height: 120, width: 'auto', marginLeft: -30 }} />
      </div>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 48 }}>
        <div><p style={{ fontSize: 14, color: 'rgba(255,255,255,.85)', lineHeight: 1.8 }}>Mit rund 45.000 Studierenden zählt die gemeinnützige FOM zu den größten Hochschulen Europas.</p></div>
        <div>
          <strong style={{ display: 'block', color: 'rgba(255,255,255,.4)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 18 }}>Partnerportal</strong>
          <a href="#stammdaten" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>Ihr Unternehmen</a>
          <a href="#teilnehmer" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>Ihre Studierenden</a>
          <a href="#rechnungen" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>Rechnungen</a>
        </div>
        <div>
          <strong style={{ display: 'block', color: 'rgba(255,255,255,.4)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 18 }}>FOM Hochschule</strong>
          <a href="https://www.fom.de" target="_blank" rel="noopener" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>Startseite</a>
          <a href="https://www.fom.de/studiengangsfinder.html" target="_blank" rel="noopener" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>Studiengänge</a>
        </div>
        <div>
          <strong style={{ display: 'block', color: 'rgba(255,255,255,.4)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 18 }}>Kontakt</strong>
          <span style={{ display: 'block', color: 'rgba(255,255,255,.75)', fontSize: 14, padding: '5px 0' }}>Leimkugelstraße 6, 45141 Essen</span>
          <a href="tel:+4920181004722" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>0201/81004-722</a>
          <a href="mailto:presse@fom.de" style={{ display: 'block', color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 14, padding: '5px 0' }}>presse@fom.de</a>
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: '40px auto 0', paddingTop: 24, borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 13, color: 'rgba(255,255,255,.4)', display: 'flex', justifyContent: 'space-between' }}>
        <span>&copy; 2026 FOM Hochschule</span>
        <span>
          <a href="https://www.fom.de/impressum.html" target="_blank" rel="noopener" style={{ color: 'rgba(255,255,255,.5)', textDecoration: 'none', fontWeight: 600 }}>Impressum</a>&nbsp;&nbsp;
          <a href="https://www.fom.de/datenschutz.html" target="_blank" rel="noopener" style={{ color: 'rgba(255,255,255,.5)', textDecoration: 'none', fontWeight: 600 }}>Datenschutz</a>
        </span>
      </div>
    </footer>
  )
}

// ═══════════════════════════════════════════════════════════════
// Exports für andere Tabs
// ═══════════════════════════════════════════════════════════════

export function FirmaKontaktdaten({ firma: _firma, primaryContact }: { firma: Firma; primaryContact: PrimaryContact | null }) {
  return (
    <>
      {primaryContact && (
        <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 mb-4 flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-3 min-w-[200px]">
            <div className="w-9 h-9 rounded-full bg-fom-50 text-fom-600 flex items-center justify-center flex-none"><User size={16} /></div>
            <div>
              <div className="text-[0.82rem] font-semibold text-gray-900 leading-tight">{primaryContact.fullname || '–'}</div>
              {primaryContact.jobtitle && <div className="text-[0.72rem] text-gray-500">{primaryContact.jobtitle}</div>}
            </div>
          </div>
          {primaryContact.emailaddress1 && <a href={`mailto:${primaryContact.emailaddress1}`} className="inline-flex items-center gap-1.5 text-[0.78rem] text-fom-700 hover:text-fom-500 font-medium no-underline"><Mail size={13} className="text-gray-400" />{primaryContact.emailaddress1}</a>}
          {primaryContact.telephone1 && <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-gray-700"><Phone size={13} className="text-gray-400" />{primaryContact.telephone1}</span>}
        </div>
      )}
    </>
  )
}

export function FirmaFirmensitz({ firma }: { firma: Firma }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
      <div className="text-sm font-semibold text-gray-900 mb-2">Firmensitz</div>
      <div className="text-sm text-gray-600 leading-relaxed">
        {firma.strasse && <div>{firma.strasse}{firma.hausnummer ? ` ${firma.hausnummer}` : ''}</div>}
        {(firma.plz || firma.stadt) && <div>{firma.plz} {firma.stadt}</div>}
        {firma.land && <div>{firma.land}</div>}
      </div>
    </div>
  )
}
